import type { $ZodTemplateLiteralPart } from 'zod/v4/core';
import { z } from 'zod/v4-mini';

const optionalNonFirst = <Literal extends $ZodTemplateLiteralPart>(
  lit: Literal,
) => {
  return z.optional(z.templateLiteral([z.literal(' '), lit]));
};

const NO_ANIMATION = 'no-animation';
const ANIMATION_NAMES = [
  'spin',
  'bounce',
  'sink',
  'hop',
  'dip',
  'strobe',
  'shake',
  'zoom',
] as const;
const DURATION_INTERVALS = ['ms', 's'] as const;
const ANIMATE_ON = ['-hover', '-active'] as const;
const ANIMATION_DIRECTION = [
  'reverse',
  'normal',
  'alternate',
  'alternate-reverse',
] as const;

const nameSchema = z.enum(ANIMATION_NAMES);
const animateOnSchema = z.templateLiteral([z.enum(ANIMATE_ON)]);

const timeSchema = z.union([
  z.number(),
  z.templateLiteral([z.number(), z.enum(DURATION_INTERVALS)]),
]);
const durationSchema = z.templateLiteral(['duration-', timeSchema]);
const delaySchema = z.templateLiteral(['delay-', timeSchema]);
const iterationSchema = z.templateLiteral([
  'iterate-',
  z.union([z.number(), z.literal('infinite')]),
]);
const directionSchema = z.templateLiteral([
  'direction-',
  z.enum(ANIMATION_DIRECTION),
]);

/** Validate passed string as being an animation
 * variant. Strings are trimmed before evaluated*/

export const animationVariantsSchema = z.pipe(
  z.string().check(z.trim()),
  z.union([
    z.templateLiteral([
      z.templateLiteral([nameSchema, z.optional(animateOnSchema)]),
      optionalNonFirst(durationSchema),
      optionalNonFirst(delaySchema),
      optionalNonFirst(iterationSchema),
      optionalNonFirst(directionSchema),
    ]),
    z.literal(NO_ANIMATION),
  ]),
);

/** Validate passed string as being an animation variant. If
 * validation fails, return null. */
export const animationVariantsCatchValue = (
  val: AnimationVariants = 'no-animation',
) => z.catch(animationVariantsSchema, val);

type AllAnimationNames = z.infer<typeof nameSchema>;
type AnimationVariants = z.infer<typeof animationVariantsSchema>;

export type { AllAnimationNames, AnimationVariants };
